import { Module } from "vuex";
import { rootState } from "../types/store/state";
import {
  fetchDashboards,
  fetchDashboardById,
  createDashboard,
  updateDashboard,
  deleteDashboard,
  deleteDashboards,
  saveDashboardWidgets,
  updateDashboardOrder,
} from "@/services/dashboardService";

export interface DashboardSummary {
  id: string;
  name: string;
  companyID: string;
  createdAt?: string;
  updatedAt?: string;
  sortOrder?: number;
  isDateFilterEnabled?: boolean;
  isTitleVisible?: boolean;
  isEditButtonVisible?: boolean;
  isExportButtonVisible?: boolean;
  createdBy?: string;
  widgetCount?: number;
  widgetIds?: string;
}

function widgetIdsOf(dash: any): string | undefined {
  if (dash?.widgetIds) return dash.widgetIds;
  if (!Array.isArray(dash?.widgets)) return undefined;
  return dash.widgets.map((w: any) => w?.widget?.id ?? w?.widgetID ?? w?.widgetId).join(",");
}

export interface DashboardsState {
  list: DashboardSummary[];
  byId: Record<string, any>;
  loading: boolean;
  lastFetchTs: number;
}

function normalizeVisibleFlag(value: unknown) {
  if (value == null) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }
  return value === true || value === 1;
}

export const dashboards: Module<DashboardsState, rootState> = {
  namespaced: true,

  state: (): DashboardsState => ({
    list: [],
    byId: {},
    loading: false,
    lastFetchTs: 0,
  }),

  getters: {
    dashboards: (s) => s.list,
    dashboardById: (s) => (id: string | number) => s.byId[id],
    loading: (s) => s.loading,
    dashboardsByCompany: (s) => (companyId: string) => s.list.filter((d: any) => d.companyID === companyId),
  },

  mutations: {
    setLoading(state, val: boolean) {
      state.loading = val;
    },
    setLastFetchTs(state, ts: number) {
      state.lastFetchTs = ts;
    },
    setList(state, dashboards: DashboardSummary[]) {
      state.list = [...dashboards].sort((a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity));
      state.list.forEach((d) => (state.byId[d.id] = d));
    },
    cacheOne(state, dash: any) {
      state.byId[dash.id] = dash;
    },
    upsert(state, dash: DashboardSummary) {
      const i = state.list.findIndex((d) => d.id === dash.id);
      if (i === -1) state.list.unshift(dash);
      else state.list.splice(i, 1, dash);
      state.byId[dash.id] = dash;
    },
    remove(state, id: string) {
      state.list = state.list.filter((d) => d.id !== id);
      delete state.byId[id];
    },
    removeMany(state, ids: string[]) {
      const deleted = new Set(ids);
      state.list = state.list.filter((d) => !deleted.has(d.id));
      ids.forEach((id) => delete state.byId[id]);
    },
    reorder(state, { companyId, ordered }: { companyId: string; ordered: DashboardSummary[] }) {
      const reordered = ordered.map((dash, i) => ({ ...dash, sortOrder: i + 1 }));
      state.list = [...state.list.filter((d) => d.companyID !== companyId), ...reordered];
    },
  },

  actions: {
    async loadAll({ state, commit }, force = false) {
      if (!force && (state.loading || Date.now() - state.lastFetchTs < 30_000)) return;
      commit("setLoading", true);
      try {
        const data = await fetchDashboards();
        commit("setList", data);
        commit("setLastFetchTs", Date.now());
      } finally {
        commit("setLoading", false);
      }
    },

    async loadOne({ state, commit }, { companyId, id }: { companyId: string; id: string }) {
      const cached = state.byId[id];
      if (cached && cached.companyID !== companyId) throw new Error("Dashboard company does not match.");
      if (!cached || !("widgets" in cached)) {
        const dash = await fetchDashboardById(id);
        if (dash.companyID !== companyId) throw new Error("Dashboard company does not match.");
        commit("cacheOne", dash);
        commit("upsert", {
          id: dash.id,
          name: dash.name,
          companyID: dash.companyID,
          createdAt: dash.createdAt,
          isDateFilterEnabled: dash.isDateFilterEnabled,
          isTitleVisible: dash.isTitleVisible,
          isEditButtonVisible: dash.isEditButtonVisible,
          isExportButtonVisible: dash.isExportButtonVisible,
          widgetCount: dash.widgetCount ?? dash.widgets?.length,
          widgetIds: widgetIdsOf(dash),
        });
        return dash;
      }
      return cached;
    },

    async save(
      { commit, state },
      { id, name, widgets, companyId, isDateFilterEnabled, isTitleVisible, isEditButtonVisible, isExportButtonVisible }
    ) {
      const dashboardName = typeof name === "string" ? name.trim() : name;
      if (!id) {
        const created = await createDashboard({
          title: dashboardName,
          companyId,
          isDateFilterEnabled,
          isTitleVisible,
          isEditButtonVisible,
          isExportButtonVisible,
          widgets: Array.isArray(widgets) ? widgets : [],
        });
        const dashId = created.id;
        const fullDash = created;
        const withFlag = {
          ...fullDash,
          isDateFilterEnabled: fullDash.isDateFilterEnabled ?? isDateFilterEnabled ?? false,
          isTitleVisible: fullDash.isTitleVisible ?? isTitleVisible ?? true,
          isEditButtonVisible: fullDash.isEditButtonVisible ?? isEditButtonVisible ?? true,
          isExportButtonVisible: fullDash.isExportButtonVisible ?? isExportButtonVisible ?? true,
        };
        commit("upsert", {
          id: withFlag.id,
          name: withFlag.name,
          companyID: withFlag.companyID,
          createdAt: withFlag.createdAt ?? new Date().toISOString(),
          isDateFilterEnabled: withFlag.isDateFilterEnabled,
          isTitleVisible: withFlag.isTitleVisible,
          isEditButtonVisible: withFlag.isEditButtonVisible,
          isExportButtonVisible: withFlag.isExportButtonVisible,
          widgetCount: withFlag.widgetCount ?? withFlag.widgets?.length,
          widgetIds: widgetIdsOf(withFlag),
        });
        commit("cacheOne", withFlag);
        return dashId;
      }
      const original = state.byId[id] ?? {};
      let latestDash: any = original;
      const titleChanged = dashboardName && dashboardName !== (original.name ?? "");
      const dateFlagChanged = isDateFilterEnabled !== undefined && isDateFilterEnabled !== original.isDateFilterEnabled;
      const titleVisibilityChanged =
        isTitleVisible !== undefined && isTitleVisible !== normalizeVisibleFlag(original.isTitleVisible);
      const editButtonVisibilityChanged =
        isEditButtonVisible !== undefined && isEditButtonVisible !== normalizeVisibleFlag(original.isEditButtonVisible);
      const exportButtonVisibilityChanged =
        isExportButtonVisible !== undefined &&
        isExportButtonVisible !== normalizeVisibleFlag(original.isExportButtonVisible);
      if (
        titleChanged ||
        dateFlagChanged ||
        titleVisibilityChanged ||
        editButtonVisibilityChanged ||
        exportButtonVisibilityChanged
      ) {
        const updated = await updateDashboard(id, {
          title: dashboardName || original.name,
          ...(dateFlagChanged ? { isDateFilterEnabled } : {}),
          ...(titleVisibilityChanged ? { isTitleVisible } : {}),
          ...(editButtonVisibilityChanged ? { isEditButtonVisible } : {}),
          ...(exportButtonVisibilityChanged ? { isExportButtonVisible } : {}),
        });
        latestDash = {
          ...(original || {}),
          ...(updated || {}),
          id,
          name: updated?.name ?? dashboardName ?? original.name,
          ...(dateFlagChanged ? { isDateFilterEnabled } : {}),
          ...(titleVisibilityChanged ? { isTitleVisible } : {}),
          ...(editButtonVisibilityChanged ? { isEditButtonVisible } : {}),
          ...(exportButtonVisibilityChanged ? { isExportButtonVisible } : {}),
        };
        commit("cacheOne", latestDash);
      }
      if (Array.isArray(widgets)) {
        const originalWidgets = original?.widgets ?? [];
        const widgetsChanged =
          widgets.length !== originalWidgets.length || JSON.stringify(widgets) !== JSON.stringify(originalWidgets);
        if (widgetsChanged) {
          await saveDashboardWidgets(id, widgets);
          latestDash = await fetchDashboardById(id);
          commit("cacheOne", latestDash);
        }
      }
      commit("upsert", {
        id,
        name: latestDash.name ?? dashboardName ?? original.name,
        companyID: latestDash.companyID ?? original.companyID,
        createdAt: latestDash.createdAt ?? original.createdAt,
        isDateFilterEnabled: isDateFilterEnabled !== undefined ? isDateFilterEnabled : latestDash.isDateFilterEnabled,
        isTitleVisible: isTitleVisible !== undefined ? isTitleVisible : latestDash.isTitleVisible,
        isEditButtonVisible: isEditButtonVisible !== undefined ? isEditButtonVisible : latestDash.isEditButtonVisible,
        isExportButtonVisible:
          isExportButtonVisible !== undefined ? isExportButtonVisible : latestDash.isExportButtonVisible,
        widgetCount: latestDash.widgetCount ?? latestDash.widgets?.length,
        widgetIds: widgetIdsOf(latestDash),
      });
      return id;
    },

    async saveWidgets({ commit }, { id, widgets }) {
      if (!id || !Array.isArray(widgets)) return;
      const fullDash = await saveDashboardWidgets(id, widgets);
      commit("upsert", {
        id: fullDash.id,
        name: fullDash.name,
        companyID: fullDash.companyID,
        createdAt: fullDash.createdAt ?? new Date().toISOString(),
        isDateFilterEnabled: fullDash.isDateFilterEnabled,
        isTitleVisible: fullDash.isTitleVisible,
        isEditButtonVisible: fullDash.isEditButtonVisible,
        isExportButtonVisible: fullDash.isExportButtonVisible,
        widgetCount: fullDash.widgetCount ?? fullDash.widgets?.length,
        widgetIds: widgetIdsOf(fullDash),
      });
      commit("cacheOne", fullDash);
    },

    async remove({ commit }, id: string) {
      await deleteDashboard(id);
      commit("remove", id);
    },

    async removeMany({ commit }, { companyId, ids }: { companyId: string; ids: string[] }) {
      await deleteDashboards(companyId, ids);
      commit("removeMany", ids);
    },

    async saveOrder({ commit }, { companyId, ordered }: { companyId: string; ordered: DashboardSummary[] }) {
      commit("reorder", { companyId, ordered });
      await updateDashboardOrder(
        companyId,
        ordered.map((d) => d.id)
      );
    },
  },
};
